import { useMemo, useState } from 'react'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import Faq from '../components/Faq'
import type { QA } from '../components/Faq'
import ResultSheet from '../components/ResultSheet'
import CalcActions from '../components/CalcActions'
import InfoTip from '../components/InfoTip'
import { Field, SmartField } from '../components/Inputs'
import { computeStair, DEFAULT_STAIR } from '../calculators/stair'
import type { StairInputs, StairFlag } from '../calculators/stair'
import { calcsInCategory } from '../data/calculators'
import { saveSheet, genId } from '../lib/storage'
import { useLocale, localizedHref } from '../i18n/locale'
import { t } from '../i18n/strings'
import { calcName, categoryName } from '../i18n/calculators'

const PATH = '/calculators/stair/'
const CATEGORY = 'outdoor-and-landscaping'

const TITLE = 'Stair Calculator — Risers, Treads & Stringer Length | RenoSheets'
const DESC = 'Free residential stair calculator with IRC code checks. Enter total rise to get exact riser height, tread count, stair stringer length and angle. SVG stair-profile diagram. No signup.'

const FAQS: QA[] = [
  { q: 'How do I calculate the number of stair risers I need?', a: 'Divide the total floor-to-floor rise by your target riser height — typically 7 to 7.5 inches for residential — and round to the nearest whole number. Then divide the total rise by that count to get the exact riser height. Every riser in the run must be the same height; building code allows a maximum variation of 3/8 inch between any two risers.' },
  { q: 'What is the IRC maximum riser height for residential stairs?', a: 'The International Residential Code (IRC R311.7.5) caps residential riser height at 7.75 inches and requires a minimum tread depth (run) of 10 inches. The calculator flags any combination that exceeds the riser limit or falls below the tread minimum so you catch it before cutting stringers.' },
  { q: 'How do I figure out the stringer length?', a: 'The stringer is the hypotenuse of the right triangle formed by the total rise and total run. Stringer length = √(total rise² + total run²). For a typical 108 in (9 ft) rise with 14 treads at 11 in each (154 in run), the stringer is about 188 in or 15 ft 8 in long — you will need 2×12 stock at minimum.' },
  { q: 'What is the 2R + T rule for comfortable stairs?', a: 'A long-standing carpenter rule: two times the riser height plus the tread depth should land between 24 and 25 inches. So a 7 in riser pairs nicely with an 11 in tread (2×7 + 11 = 25). Stairs outside the 24-25 range feel cramped (too steep) or stretched (too shallow), even when each measurement is within code.' },
  { q: 'How wide should an indoor staircase be?', a: 'IRC requires a minimum stair width of 36 inches measured above the handrail, with handrails projecting no more than 4.5 inches into that space. 36 inches is the legal minimum; 42 to 48 inches is more comfortable for moving furniture and feels less tight for two people passing.' },
  { q: 'What size lumber do I need for stair stringers?', a: 'Stair stringers are almost always 2×12 stock — anything narrower leaves too little material between the notch cuts and weakens the stringer. Use pressure-treated lumber for exterior stairs and any stair in contact with concrete. Plan on a minimum of three stringers for treads up to 36 inches wide and four for wider stairs.' },
]

interface StairDiagramProps {
  riserCount: number
  riserHeightIn: number
  treadDepthIn: number
  noseIn: number
}

function StairDiagram({ riserCount, riserHeightIn, treadDepthIn, noseIn }: StairDiagramProps) {
  // Profile view of the stair: each step is a riser + tread. We scale to fit.
  const W = 360
  const H = 240
  const padX = 30
  const padY = 25
  const totalRise = riserCount * riserHeightIn
  const totalRun = Math.max(0, riserCount - 1) * treadDepthIn
  if (totalRise <= 0 || totalRun <= 0 || riserCount < 2) {
    return <div className="text-xs text-text-subtle">Enter measurements to see the stair profile.</div>
  }
  const maxW = W - padX * 2
  const maxH = H - padY * 2
  const ratio = Math.min(maxW / totalRun, maxH / totalRise)
  const rStep = treadDepthIn * ratio
  const rHeight = riserHeightIn * ratio
  // Draw from bottom-left; build polyline along the stair profile
  const baseX = padX
  const baseY = H - padY
  const points: string[] = [`${baseX},${baseY}`]
  for (let s = 0; s < riserCount; s++) {
    const x = baseX + s * rStep
    const y = baseY - s * rHeight
    points.push(`${x},${y}`) // top of last tread / start of next riser
    points.push(`${x},${y - rHeight}`) // up the next riser
  }
  // close the profile with a horizontal across the top
  const lastX = baseX + (riserCount - 1) * rStep + rStep
  const topY = baseY - riserCount * rHeight
  points.push(`${lastX},${topY}`)
  points.push(`${lastX},${baseY}`)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-md" role="img" aria-label="Stair profile diagram">
      <polygon points={points.join(' ')} fill="#dcfce7" stroke="#166534" strokeWidth="2" />
      {/* Stringer hypotenuse for reference */}
      <line x1={baseX} y1={baseY} x2={lastX} y2={topY} stroke="#15803d" strokeWidth="1" strokeDasharray="3 3" />
      <text x={W / 2} y={16} textAnchor="middle" fontSize="11" fill="#0f172a" fontWeight="600">
        {riserCount} risers × {riserHeightIn.toFixed(2)} in · tread {treadDepthIn} in + {noseIn} in nose
      </text>
    </svg>
  )
}

function FlagPill({ flag }: { flag: StairFlag }) {
  const palette: Record<StairFlag['level'], string> = {
    ok: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    warn: 'bg-amber-50 text-amber-900 border-amber-200',
    fail: 'bg-red-50 text-red-800 border-red-200',
  }
  const icon: Record<StairFlag['level'], string> = { ok: '✓', warn: '!', fail: '✕' }
  return (
    <div className={`rounded-md border px-3 py-2 text-xs ${palette[flag.level]}`}>
      <strong className="mr-1">{icon[flag.level]}</strong>
      {flag.message}
    </div>
  )
}

export default function StairCalculator() {
  const locale = useLocale()
  const L = (en: string) => localizedHref(en, locale)
  const tr = (k: string) => t(locale, k)

  const [i, setI] = useState<StairInputs>(DEFAULT_STAIR)
  const [saved, setSaved] = useState(false)
  const r = useMemo(() => computeStair(i, locale), [i, locale])
  const set = (patch: Partial<StairInputs>) => {
    setI((p) => ({ ...p, ...patch }))
    setSaved(false)
  }

  const onSave = async () => {
    await saveSheet({
      id: genId(),
      type: 'stair',
      name: `Stair — ${i.totalRiseIn} in rise · ${r.riserCount} risers`,
      createdAt: new Date().toISOString(),
      summary: `${r.riserHeightIn.toFixed(2)} in riser · ${r.treadCount} treads · ${r.angleDeg}°`,
      lines: r.lines.filter((l) => l.kind === 'consumable').map((l) => ({ label: l.label, qty: l.qty, unit: l.unit })),
    })
    setSaved(true)
  }

  const related = calcsInCategory(CATEGORY).filter((x) => x.slug !== 'stair')
  const assumptions = `${i.totalRiseIn} in total rise · ${r.riserCount} risers · ${i.treadDepthIn} in tread + ${i.noseIn} in nose.`

  // Step-by-step "How we got this" content for the ⓘ button on the result card.
  // Mirrors computeStair: risers = round(rise / target), riser height = rise / risers,
  // treads = risers − 1, run = treads × tread depth, stringer = √(rise² + run²),
  // angle = arctan(rise / run).
  const buildExplain = (): React.ReactNode => (
    <ol className="list-decimal space-y-1 pl-5">
      <li>
        {locale === 'fr'
          ? `Contremarches = arrondi(${i.totalRiseIn} ÷ ${i.targetRiserIn})`
          : `Risers = round(${i.totalRiseIn} ÷ ${i.targetRiserIn})`}
        {' '}= <strong>{r.riserCount}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Hauteur exacte = ${i.totalRiseIn} ÷ ${r.riserCount}`
          : `Exact riser height = ${i.totalRiseIn} ÷ ${r.riserCount}`}
        {' '}= <strong>{r.riserHeightIn.toFixed(3)} {locale === 'fr' ? 'po' : 'in'}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Marches = ${r.riserCount} − 1`
          : `Treads = ${r.riserCount} − 1`}
        {' '}= <strong>{r.treadCount}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Course totale = ${r.treadCount} × ${i.treadDepthIn}`
          : `Total run = ${r.treadCount} × ${i.treadDepthIn}`}
        {' '}= <strong>{r.totalRunIn.toFixed(2)} {locale === 'fr' ? 'po' : 'in'}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Limon = √(${i.totalRiseIn}² + ${r.totalRunIn.toFixed(2)}²)`
          : `Stringer = √(${i.totalRiseIn}² + ${r.totalRunIn.toFixed(2)}²)`}
        {' '}= <strong>{r.stringerLengthIn.toFixed(2)} {locale === 'fr' ? 'po' : 'in'}</strong>
      </li>
      <li>
        {locale === 'fr'
          ? `Angle = arctan(${i.totalRiseIn} ÷ ${r.totalRunIn.toFixed(2)})`
          : `Angle = arctan(${i.totalRiseIn} ÷ ${r.totalRunIn.toFixed(2)})`}
        {' '}= <strong>{r.angleDeg.toFixed(1)}°</strong>
      </li>
    </ol>
  )

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <Seo title={TITLE} description={DESC} path={PATH} schema={softwareAppSchema(calcName('stair', 'Stair Calculator', locale), DESC, localizedHref(PATH, locale))} />
      <Breadcrumbs
        items={[
          { name: tr('crumb.home'), href: L('/') },
          { name: categoryName(CATEGORY, 'Outdoor & Landscaping', locale), href: L(`/calculators/${CATEGORY}/`) },
          { name: calcName('stair', 'Stair Calculator', locale), href: L(PATH) },
        ]}
      />

      <h1 className="text-3xl">Stair Calculator</h1>
      <p className="mt-1 max-w-content text-text-muted">Enter total rise and tread depth to get exact riser height, stringer length, stair angle and IRC code-compliance flags — plus a profile diagram.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <form className="rounded-xl border border-border bg-surface p-5 lg:col-span-3" onSubmit={(e) => e.preventDefault()}>
          <div className="rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              Total rise (floor to floor)
              <InfoTip text="Vertical distance from finished lower floor to finished upper floor. Measure plumb, not along the wall." />
            </p>
            <SmartField label="Rise" imperialValue={i.totalRiseIn} onChangeImperial={(n) => set({ totalRiseIn: n })} imperialUnit="in" step={0.5} />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-md border border-border p-3">
              <p className="mb-1 text-sm font-medium text-text-body">
                Target riser
                <InfoTip text="Preferred riser height. The calculator rounds the riser count to honor this target. Most builders aim for 7 to 7.5 in." />
              </p>
              <SmartField label="Target" imperialValue={i.targetRiserIn} onChangeImperial={(n) => set({ targetRiserIn: n })} imperialUnit="in" step={0.25} />
            </div>
            <div className="rounded-md border border-border p-3">
              <p className="mb-1 text-sm font-medium text-text-body">
                Tread depth
                <InfoTip text="Horizontal depth of each tread, not counting the nosing. IRC minimum is 10 in for residential." />
              </p>
              <SmartField label="Tread" imperialValue={i.treadDepthIn} onChangeImperial={(n) => set({ treadDepthIn: n })} imperialUnit="in" step={0.25} />
            </div>
          </div>

          <div className="mt-4 rounded-md border border-border p-3">
            <p className="mb-1 text-sm font-medium text-text-body">
              Tread nosing
              <InfoTip text="Distance the tread overhangs the riser below. Typical residential is 3/4 to 1-1/4 in. IRC requires 3/4 to 1-1/4 in for treads under 11 in deep." />
            </p>
            <SmartField label="Nose" imperialValue={i.noseIn} onChangeImperial={(n) => set({ noseIn: n })} imperialUnit="in" step={0.125} />
          </div>

          <div className="mt-5 rounded-lg bg-surface-2 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Calculated stair</p>
            <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <li>Risers: <strong className="tnum">{r.riserCount}</strong></li>
              <li>Riser height: <strong className="tnum">{r.riserHeightIn.toFixed(3)} in</strong></li>
              <li>Treads: <strong className="tnum">{r.treadCount}</strong></li>
              <li>Total run: <strong className="tnum">{r.totalRunIn.toFixed(2)} in</strong></li>
              <li>Stringer: <strong className="tnum">{r.stringerLengthIn.toFixed(2)} in</strong></li>
              <li>Angle: <strong className="tnum">{r.angleDeg.toFixed(1)}°</strong></li>
            </ul>
          </div>

          <div className="mt-4 space-y-2">
            {r.flags.map((f, k) => <FlagPill key={k} flag={f} />)}
          </div>

          <div className="mt-5 flex flex-col items-center rounded-lg bg-surface-2 p-3">
            <StairDiagram
              riserCount={r.riserCount}
              riserHeightIn={r.riserHeightIn}
              treadDepthIn={i.treadDepthIn}
              noseIn={i.noseIn}
            />
            <p className="mt-2 text-xs text-text-subtle">Side profile — risers vertical, treads horizontal. Dashed line shows the stringer line of run.</p>
          </div>
          <CalcActions onSave={onSave} saved={saved} locale={locale} className="mt-6 border-t border-border pt-4" />
        </form>

        <div className="lg:col-span-2">
          <ResultSheet
            primary={{ name: 'Stringer length', label: `${r.riserCount} risers × ${r.riserHeightIn.toFixed(2)} in`, qty: r.stringerLengthIn, unit: 'in', query: 'stair stringer 2x12 pressure treated' }}
            secondary={{ name: 'Treads', label: `at ${i.treadDepthIn} in deep`, qty: r.treadCount, unit: 'piece', query: 'stair tread oak pine pre-cut' }}
            lines={r.lines}
            assumptions={assumptions}
            explain={buildExplain()}
          />
        </div>
      </div>

      <article className="mx-auto mt-8 max-w-content text-text-body">
        <h2 className="text-2xl">{locale === 'fr' ? 'Ce que le code du bâtiment exige vraiment pour un escalier' : 'What building code actually requires for a stair'}</h2>
        <p className="mt-3">
          {locale === 'fr'
            ? 'Les dimensions d’escalier sont régies par le code du bâtiment de façon beaucoup plus stricte que la plupart des autres éléments de charpente, car des marches inégales sont une cause majeure de chutes. La plupart des codes limitent la hauteur de contremarche à environ 7 3/4 pouces et exigent une profondeur de giron minimale d’environ 10 pouces, et surtout, chaque contremarche d’un même palier doit être à moins de 3/16 de pouce de toutes les autres, car le pied s’attend à la même hauteur de marche à chaque fois. Cette exigence de constance est la raison pour laquelle le calcul d’escalier part de la montée totale, pas d’une hauteur de contremarche cible : le calculateur divise la montée totale par le nombre entier de contremarches le plus proche qui garde chacune conforme au code, plutôt que de vous laisser choisir une hauteur qui ne se divise pas également. Le dégagement de tête est souvent la contrainte oubliée lors d’une rénovation : le code exige généralement au moins 6 pi 8 po de dégagement mesuré verticalement depuis le nez de chaque marche, et charpenter un escalier sans vérifier le dégagement par rapport à la structure de plancher au-dessus est l’une des erreurs de construction d’escalier les plus courantes — et coûteuses. La taille et l’emplacement des paliers ont aussi des minimums là où un escalier change de direction ou dépasse un certain nombre de contremarches dans une même volée, alors un escalier long et abrupt ne peut souvent pas être une seule volée droite même s’il y a de la place pour une.'
            : 'Stair dimensions are governed by building code far more tightly than most other framing elements, because inconsistent steps are a leading cause of falls. Most codes cap riser height around 7 3/4 inches and require a minimum tread depth around 10 inches, and — critically — every riser in a single flight has to be within about 3/16 inch of every other riser, since your foot expects the same step height every time. That consistency requirement is why stair math starts from total rise, not a target riser height: the calculator divides the total rise by the nearest whole number of risers that keeps each one code-compliant, rather than letting you pick a riser height that doesn\'t divide evenly. Headroom is often the constraint that gets missed on a retrofit: code typically requires at least 6 ft 8 in of clearance measured vertically from the nose of each tread, and framing a staircase without checking headroom against the floor structure above is one of the more common — and expensive — stair-building mistakes. Landing size and location also have minimums where a stair changes direction or exceeds a certain number of risers in one run, so long, steep stairs often can\'t be a single straight flight even if there\'s room for one.'}
        </p>
        <h2 className="mt-8 text-2xl">How to calculate stairs</h2>
        <p className="mt-3">Stair design starts with the total rise — the vertical distance from the finished lower floor to the finished upper floor. Everything else is derived from it:</p>
        <pre className="mt-3 overflow-x-auto rounded-lg bg-surface-2 p-3 text-sm text-text">{`riserCount = round(totalRise ÷ targetRiser)   ← never fewer than 2
riserHeight = totalRise ÷ riserCount           ← every riser must match
treadCount = riserCount − 1                    ← upper floor is the "top tread"
totalRun = treadCount × treadDepth
stringer = √(totalRise² + totalRun²)
angle = arctan(totalRise ÷ totalRun)`}</pre>
        <p className="mt-3">The IRC residential limits are 7.75 in max riser, 10 in min tread, 3/8 in max variation between any two risers or any two treads, and 6 ft 8 in minimum headroom. Comfort lives in a narrower band: 7 to 7.5 in risers paired with 10.5 to 11.5 in treads.</p>

        <h3 className="mt-6 text-xl">Common mistakes</h3>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          <li><strong>Measuring rise to the subfloor.</strong> Always measure to the finished floor surface — flooring thickness on the upper or lower level changes the top and bottom riser heights.</li>
          <li><strong>Letting risers vary.</strong> Code allows 3/8 in variation between the largest and smallest riser in the run. Anything more is a trip hazard you will feel every time you walk it.</li>
          <li><strong>Cutting all three stringers from one layout line.</strong> Slight saw drift compounds. Mark each stringer individually with a framing square and stair gauges, then test-fit before installing treads.</li>
          <li><strong>Forgetting the top tread is the floor.</strong> Number-of-treads is always one less than number-of-risers. Buy treads accordingly or you will end up with an extra board.</li>
        </ul>
      </article>

      <Faq items={FAQS} />

      <section className="mt-10">
        <h2 className="text-2xl">Related calculators</h2>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {related.map((x) => (
            <a
              key={x.slug}
              href={L(`/calculators/${x.slug}/`)}
              className={`rounded-lg border p-3 text-sm transition-colors ${
                x.live
                  ? 'border-border bg-surface-2 font-medium text-brand-text hover:border-brand hover:bg-surface'
                  : 'border-dashed border-border bg-surface-2 text-text-muted'
              }`}
            >
              {calcName(x.slug, x.name, locale)} {x.live ? '' : <span className="text-text-subtle">{tr('calc.soon')}</span>}
            </a>
          ))}
        </div>
      </section>

      <div className="mx-auto mt-8 max-w-content border-t border-border pt-4 text-xs text-text-subtle">
        Reviewed by the RenoSheets team for calculation accuracy. Method: integer riser count from rounded division, exact riser height from total rise ÷ count, IRC R311.7.5 residential limits flagged not enforced. Last updated 2026-06-02. Estimates only — verify dimensions against your local building code and field measurements before cutting stringers.
      </div>
    </div>
  )
}
